import { VERSION } from 'config';
import Logger from 'modules/Logger/service';
import { CustomResponseHeaders, CacheParams } from './types';

const VERSION_PREFIX = 'version';

class CacheStorageService {
  private currentVersionCache?: Cache;

  public async init() {
    if (!window.CacheStorage) {
      Logger.error('Your browser does not support CacheStorage API');

      return;
    }

    const currentVersion = `${VERSION_PREFIX}${VERSION}`;
    const isCurrentCacheExist = await caches.has(currentVersion);
    this.currentVersionCache = await caches.open(currentVersion);

    if (!isCurrentCacheExist) {
      // Remove all old versions of caches;
      const cachesKeys = await caches.keys();

      await Promise.all(cachesKeys.map((key) => {
        if (key.startsWith(VERSION_PREFIX) && key !== currentVersion) {
          return caches.delete(key);
        }

        return true;
      }));
    }
  }

  public async putToCache(
    cacheParams: CacheParams,
    requestConfig: Request,
  ): Promise<Response> {
    const getData = () =>
      this.cacheAndGetNewResponse(cacheParams, requestConfig);

    if (!this.currentVersionCache) {
      return fetch(requestConfig);
    }

    // Handle 'existence' response case
    const response = await this.currentVersionCache.match(
      requestConfig.clone(),
      cacheParams.cacheQueryOptions,
    );

    if (!response) {
      return getData();
    }

    // Handle 'lifetime' case
    if (cacheParams.lifetime) {
      const responseCreationTime =
        response.headers.get(CustomResponseHeaders.Date);
      const currentTime = new Date().getTime();

      if (!responseCreationTime ||
        currentTime - Number(responseCreationTime) > cacheParams.lifetime
      ) {
        await this.currentVersionCache.delete(
          requestConfig.clone(),
          cacheParams.cacheQueryOptions,
        );

        return getData();
      }
    }

    return response;
  }

  private async cacheAndGetNewResponse(
    cacheParams: CacheParams,
    requestConfig: Request,
  ) {
    // Handle new response
    const response = await fetch(requestConfig);
    const newResponse = response.clone();
    const transformedResponse: HttpResponse<unknown> =
      (await newResponse[cacheParams.responseType]());

    // Handle error case - we do not need to add response if got an error
    if (newResponse.status === 401 ||
      !transformedResponse.success ||
      transformedResponse.error
    ) {
      return newResponse;
    }

    const responseForSaving =
      CacheStorageService.getResponseWithDateHeader(response);

    if (this.currentVersionCache) {
      await this.currentVersionCache.put(
        requestConfig.clone(),
        responseForSaving,
      );
    }

    return response.clone();
  }

  static getResponseWithDateHeader(response: Response) {
    const cloneResponse = response.clone();
    const newHeaders = new Headers(cloneResponse.headers);
    newHeaders.set(CustomResponseHeaders.Date, new Date().getTime().toString());

    return new Response(cloneResponse.body, {
      headers: newHeaders,
    });
  }
}

export default new CacheStorageService();
